import { useEffect } from 'react';
import type { Step } from '../components/layout/AppShell';

/**
 * Per-step URLs.
 *
 * Each step gets a real path so the form has its own link, the browser Back
 * button works, and a step can be shared or bookmarked. The step state stays
 * the source of truth; this only keeps the address bar in sync with it.
 */

export const STEP_PATHS: Record<Step, string> = {
  dashboard: '/',
  requests: '/requests',
  input: '/new',
  review: '/form',
  destination: '/destination',
  processing: '/processing',
  success: '/success',
};

const PATH_TO_STEP: Record<string, Step> = Object.fromEntries(
  Object.entries(STEP_PATHS).map(([step, path]) => [path, step as Step]),
) as Record<string, Step>;

/** The step a path represents, or null when the path is not one of ours. */
export function stepForPath(pathname: string): Step | null {
  const normalised = pathname.replace(/\/+$/, '') || '/';
  return PATH_TO_STEP[normalised] ?? null;
}

/**
 * Keeps the URL and the current step in sync.
 *
 * `canEnter` guards a step whose data is only held in memory: navigating
 * straight to /form in a fresh tab has no draft to render, so the user is sent
 * back to the start rather than shown an empty form.
 */
export function useStepRouting(
  step: Step,
  setStep: (step: Step) => void,
  canEnter: (step: Step) => boolean,
): void {
  // Push the path whenever the step changes.
  useEffect(() => {
    const target = STEP_PATHS[step];
    if (window.location.pathname === target) return;

    // Replace rather than push for the start step, so returning to it does not
    // build up history entries the user has to click through.
    if (step === 'input') window.history.replaceState({ step }, '', target);
    else window.history.pushState({ step }, '', target);
  }, [step]);

  // Follow browser Back/Forward.
  useEffect(() => {
    const onPopState = () => {
      const target = stepForPath(window.location.pathname);
      if (!target) {
        setStep('input');
        return;
      }
      setStep(canEnter(target) ? target : 'input');
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [setStep, canEnter]);
}
